// BookTickets/stages/QuantitySelection/components/TicketType.tsx
'use client'

import { useCallback, useMemo } from 'react'
import cn from 'classnames'

import styles from './index.module.scss'
import MinusIcon from './minus-icon.svg'
import PlusIcon from './plus-icon.svg'

import { useBookingState } from '@booking/state/booking.context'
import { ticketCount } from '@booking/lib/cart'

import type { TicketRule, TicketVariant } from '@booking/types/bookingConfig'

type TicketTypeProps = {
  variant: TicketVariant

  /**
   * Controlled value (source of truth is reducer state).
   */
  value: number

  /**
   * Optional per-ticket constraints passed from parent.
   * If omitted, we fall back to config rules from state.config.rules[variant] (if present).
   */
  restrictions?: TicketRule | null

  /**
   * Max total tickets allowed across all variants.
   */
  maxGlobal: number

  /**
   * Called only on user intent (+/-), with the next value.
   */
  onChange: (nextValue: number) => void

  disabled?: boolean
  onExceedMax?: () => void

  /**
   * When true, the + button is silently disabled when the global max is reached
   * instead of calling onExceedMax. Used when the max is driven by extras selection.
   */
  disableAtGlobalMax?: boolean
}

/**
 * TicketType (controlled)
 * - No local counter state
 * - Computes constraints from (restrictions > config.rules > defaults)
 * - Computes "global max" from booking numbers
 * - Calls onChange(next) only in click handlers (no effect loops)
 */
export default function TicketType({
  variant,
  value,
  restrictions = null,
  maxGlobal,
  onChange,
  disabled = false,
  onExceedMax,
  disableAtGlobalMax = false,
}: TicketTypeProps) {
  const state = useBookingState()

  const ruleFromConfig = useMemo<TicketRule | null>(() => {
    const rules = state.config?.rules
    if (!rules) return null
    return (rules[variant] as TicketRule | undefined) ?? null
  }, [state.config?.rules, variant])

  const resolvedMin = restrictions?.min ?? ruleFromConfig?.min ?? 0
  const resolvedMax = restrictions?.max ?? ruleFromConfig?.max

  const minReached = value <= resolvedMin
  const maxReached = typeof resolvedMax === 'number' ? value >= resolvedMax : false

  const globalCount = useMemo(() => ticketCount(state.quantities), [state.quantities])

  // True when adding one more ticket would exceed the global cap
  const globalMaxReached = globalCount >= maxGlobal

  const handleRemove = useCallback(() => {
    if (disabled || minReached) return
    onChange(value - 1)
  }, [disabled, minReached, onChange, value])

  const handleAdd = useCallback(() => {
    if (disabled || maxReached) return
    if (globalMaxReached) {
      if (!disableAtGlobalMax) onExceedMax?.()
      return
    }
    onChange(value + 1)
  }, [disabled, maxReached, globalMaxReached, disableAtGlobalMax, onChange, onExceedMax, value])

  return (
    <span className={cn(styles.controlsHolder)}>
      <button
        type="button"
        className={cn(styles.buttonMath, styles.buttonMinus)}
        disabled={disabled || minReached}
        onClick={handleRemove}
        data-id="decrease"
        aria-label={`Decrease ${variant}`}
      >
        <MinusIcon />
      </button>

      <span className={styles.controlValue}>{value}</span>

      <button
        type="button"
        className={cn(styles.buttonMath, styles.buttonPlus)}
        disabled={disabled || maxReached || (disableAtGlobalMax && globalMaxReached)}
        onClick={handleAdd}
        data-id="increase"
        aria-label={`Increase ${variant}`}
      >
        <PlusIcon />
      </button>
    </span>
  )
}
